import { useState, useEffect } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import InventoryFieldsBuilder from "./components/InventoryFieldsBuilder";
import SellingTypesBuilder from "./components/SellingTypesBuilder";
import { InventoryField } from "@/mocks/inventoryFieldsData";
import { SellingType } from "@/mocks/sellingTypesData";
import { fetchApi } from "@/services/apiService";
import { useToast } from "@/components/base/Toast";

export default function ProjectsSettingsPage() {
  const { showToast } = useToast();
  const [inventoryFields, setInventoryFields] = useState<InventoryField[]>([]);
  const [sellingTypes, setSellingTypes] = useState<SellingType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const loadSettings = async () => {
      try {
        const [fieldsData, typesData] = await Promise.all([
          fetchApi<InventoryField[]>('/projects/settings/inventory-fields', undefined, []),
          fetchApi<SellingType[]>('/projects/settings/selling-types', undefined, []),
        ]);
        if (isMounted) {
          setInventoryFields(Array.isArray(fieldsData) ? fieldsData : []);
          setSellingTypes(Array.isArray(typesData) ? typesData : []);
        }
      } catch (err) {
        console.error("Failed to load project settings from API:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadSettings();
    return () => { isMounted = false; };
  }, []);

  const handleSaveInventoryFields = async (updatedFields: InventoryField[]) => {
    try {
      const saved = await fetchApi<InventoryField[]>('/projects/settings/inventory-fields', {
        method: 'POST',
        body: JSON.stringify(updatedFields),
      });
      setInventoryFields(Array.isArray(saved) ? saved : updatedFields);
    } catch (err: any) {
      showToast(err.message || "Failed to save inventory fields", "error");
    }
  };

  const handleSaveSellingTypes = async (updatedTypes: SellingType[]) => {
    try {
      const saved = await fetchApi<SellingType[]>('/projects/settings/selling-types', {
        method: 'POST',
        body: JSON.stringify(updatedTypes),
      });
      setSellingTypes(Array.isArray(saved) ? saved : updatedTypes);
    } catch (err: any) {
      showToast(err.message || "Failed to save selling types", "error");
    }
  };

  return (
    <CrmLayout title="Project Settings">
      <div className="p-5 md:p-6 space-y-5">
        {/* Page Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">Project Settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure inventory fields and selling types for your projects
          </p>
        </div>

        {/* Info Note */}
        <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-indigo-600 text-lg w-5 h-5 flex items-center justify-center" />
            </div>
            <div className="text-sm text-gray-700">
              <span className="font-medium text-gray-800">Set up in this order:</span>
              <br />
              1️⃣ First add your <strong>Inventory Fields</strong>
              <br />
              2️⃣ Then create your <strong>Selling Types</strong> and assign fields to each type.
              <br />
              These types appear when creating a new project.
            </div>
          </div>
        </div>

        {/* Section A: Project Inventory Fields */}
        <InventoryFieldsBuilder
          inventoryFields={inventoryFields}
          onFieldsChange={setInventoryFields}
          onSave={handleSaveInventoryFields}
        />

        {/* Section B: What You're Selling */}
        <SellingTypesBuilder
          sellingTypes={sellingTypes}
          inventoryFields={inventoryFields}
          onTypesChange={setSellingTypes}
          onSave={handleSaveSellingTypes}
        />
      </div>
    </CrmLayout>
  );
}